/**
 * Per-tenant document theme (#536): the bounded set of values a theme may
 * take. The API validates against it, the settings screen offers it and the
 * PDF renderers draw it; a value outside it is never stored and never drawn.
 *
 * On the certified invoice the theme colours the « CLIENT », « ARTICLES »
 * and « RÉCAPITULATIF FISCAL » titles and the header rule (accent), tints
 * the table header band (secondary), and places and sizes the logo inside
 * the issuer column. Every DGI mention keeps its text, font, size, colour
 * and position.
 */
/** Text colour every themed title keeps when the accent is the default. */
export const DOCUMENT_THEME_DEFAULT_TITLE_COLOR = "#374151";
/** Share of the secondary colour mixed over white for the table band. */
export const DOCUMENT_THEME_TINT_RATIO = 0.12;
/** WCAG 2.x SC 1.4.3 (AA): accent text against white. */
export const DOCUMENT_THEME_MIN_ACCENT_CONTRAST = 4.5;
/** WCAG 2.x SC 1.4.6 (AAA): table header text against its band. */
export const DOCUMENT_THEME_MIN_BAND_CONTRAST = 7;
/**
 * Palette ids with their French label and colour. `DEFAULT` carries no
 * colour: each role then keeps the colour it is drawn in without a theme.
 * Red and rose are left out so no title reads like a credit-note badge.
 */
export const DOCUMENT_THEME_COLORS = {
    DEFAULT: { label: "Par défaut", hex: null },
    NAVY: { label: "Bleu marine", hex: "#1e3a8a" },
    BLUE: { label: "Bleu", hex: "#1d4ed8" },
    CYAN: { label: "Cyan", hex: "#0e7490" },
    TEAL: { label: "Bleu canard", hex: "#0f766e" },
    GREEN: { label: "Vert", hex: "#15803d" },
    OLIVE: { label: "Olive", hex: "#4d7c0f" },
    GOLD: { label: "Or", hex: "#a16207" },
    BROWN: { label: "Brun", hex: "#92400e" },
    ORANGE: { label: "Orange", hex: "#c2410c" },
    PURPLE: { label: "Violet", hex: "#6d28d9" },
    SLATE: { label: "Ardoise", hex: "#334155" },
    BLACK: { label: "Noir", hex: "#111827" },
};
export const DOCUMENT_THEME_COLOR_IDS = Object.keys(DOCUMENT_THEME_COLORS);
/** Where the logo sits inside the column that holds it. */
export const DOCUMENT_LOGO_PLACEMENTS = {
    LEFT: { label: "À gauche" },
    CENTER: { label: "Au centre" },
    RIGHT: { label: "À droite" },
};
export const DOCUMENT_LOGO_PLACEMENT_IDS = Object.keys(DOCUMENT_LOGO_PLACEMENTS);
/** Box the logo is scaled into, in points, its proportions kept. */
export const DOCUMENT_LOGO_SIZES = {
    S: { label: "Petit", width: 90, height: 45 },
    M: { label: "Moyen", width: 120, height: 60 },
    L: { label: "Grand", width: 160, height: 80 },
};
export const DOCUMENT_LOGO_SIZE_IDS = Object.keys(DOCUMENT_LOGO_SIZES);
/**
 * Largest logo accepted, in pixels. A logo prints at 160 × 80 pt at most,
 * about 670 × 335 px at 300 dpi; PDFKit decodes every pixel of a PNG each
 * time it embeds one, so a small file declaring a large image (a
 * decompression bomb) would cost seconds and gigabytes per document.
 */
export const DOCUMENT_LOGO_MAX_SIDE_PX = 2_000;
export const DOCUMENT_LOGO_MAX_PIXELS = 2_000_000;
/**
 * Largest logo file, as uploaded and as stored (the canonical form of a PNG
 * included): it bounds what a renderer inflates and re-compresses.
 */
export const DOCUMENT_LOGO_MAX_BYTES = 500_000;
/** Largest decoded logo, in bytes of 8-bit RGBA (width × height × 4). */
export const DOCUMENT_LOGO_MAX_DECODED_BYTES = DOCUMENT_LOGO_MAX_PIXELS * 4;
/** Whether a logo of this pixel size may be stored and drawn. */
export function isDocumentLogoSizeAllowed(size) {
    return (size.width > 0 &&
        size.height > 0 &&
        size.width <= DOCUMENT_LOGO_MAX_SIDE_PX &&
        size.height <= DOCUMENT_LOGO_MAX_SIDE_PX &&
        size.width * size.height <= DOCUMENT_LOGO_MAX_PIXELS);
}
/** The theme every document prints without one: today's look. */
export const DEFAULT_DOCUMENT_THEME = {
    accent: "DEFAULT",
    secondary: "DEFAULT",
    logoPlacement: "LEFT",
    logoSize: "M",
};
const ALLOWED = {
    accent: DOCUMENT_THEME_COLOR_IDS,
    secondary: DOCUMENT_THEME_COLOR_IDS,
    logoPlacement: DOCUMENT_LOGO_PLACEMENT_IDS,
    logoSize: DOCUMENT_LOGO_SIZE_IDS,
};
/** Whether `value` belongs to the bounded set of `field`. */
export function isDocumentThemeValue(field, value) {
    return typeof value === "string" && ALLOWED[field].includes(value);
}
/**
 * The theme a renderer draws from stored values: each field outside the
 * bounded set (a newer version read by an older build, a value written by
 * hand) falls back to the default for that field alone.
 */
export function resolveDocumentThemeValues(stored) {
    const pick = (field) => (isDocumentThemeValue(field, stored?.[field]) ? stored[field] : DEFAULT_DOCUMENT_THEME[field]);
    return {
        accent: pick("accent"),
        secondary: pick("secondary"),
        logoPlacement: pick("logoPlacement"),
        logoSize: pick("logoSize"),
    };
}
function channels(hex) {
    const value = hex.replace("#", "");
    return [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16));
}
/** `hex` mixed at `ratio` over white, as `#rrggbb`. */
export function documentThemeTint(hex, ratio = DOCUMENT_THEME_TINT_RATIO) {
    return ("#" +
        channels(hex)
            .map((c) => Math.round(255 - ratio * (255 - c)).toString(16).padStart(2, "0"))
            .join(""));
}
/** WCAG 2.x relative luminance of an sRGB colour. */
export function relativeLuminance(hex) {
    const [r, g, b] = channels(hex).map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG 2.x contrast ratio of two colours, from 1 to 21. */
export function contrastRatio(a, b) {
    const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}
/**
 * Whether a colour may join the palette: as accent text it reaches 4.5:1 on
 * white, and as a band its tint leaves the header text at 7:1 or more.
 */
export function meetsDocumentThemeContrast(hex) {
    return (contrastRatio(hex, "#ffffff") >= DOCUMENT_THEME_MIN_ACCENT_CONTRAST &&
        contrastRatio(DOCUMENT_THEME_DEFAULT_TITLE_COLOR, documentThemeTint(hex)) >= DOCUMENT_THEME_MIN_BAND_CONTRAST);
}
//# sourceMappingURL=document-theme.js.map